import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const AudioPlayer = ({ url }) => {
  const { theme } = useTheme();
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!url || !waveformRef.current) return;

    // Initialize WaveSurfer
    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: theme.accent,
      progressColor: theme.primary,
      cursorColor: theme.text,
      barWidth: 2,
      barRadius: 3,
      cursorWidth: 1,
      height: 80,
      barGap: 3,
      normalize: true,
      url: url
    });

    wavesurfer.current = ws;

    // Add event listeners
    ws.on('ready', () => {
      setDuration(ws.getDuration());
      setIsLoading(false);
    });

    ws.on('timeupdate', (currentTime) => {
      setCurrentTime(currentTime);
    });

    ws.on('finish', () => {
      setIsPlaying(false);
    });

    ws.on('error', (err) => {
      console.error('WaveSurfer error:', err);
      setIsLoading(false);
    });

    return () => {
      ws.destroy();
    };
  }, [url, theme]);

  const formatTime = (time) => {
    if (!time) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (wavesurfer.current && !isLoading) {
      wavesurfer.current.playPause();
      setIsPlaying(!isPlaying);
    }
  };

  const skipTime = (seconds) => {
    if (wavesurfer.current && !isLoading) {
      const currentTime = wavesurfer.current.getCurrentTime();
      const newTime = Math.max(0, Math.min(currentTime + seconds, duration));
      wavesurfer.current.setTime(newTime);
    }
  };

  const toggleMute = () => {
    if (wavesurfer.current && !isLoading) {
      if (isMuted) {
        wavesurfer.current.setVolume(volume);
      } else {
        wavesurfer.current.setVolume(0);
      }
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (wavesurfer.current && !isLoading) {
      wavesurfer.current.setVolume(newVolume);
      setVolume(newVolume);
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Waveform */}
      <div className="relative">
        <div ref={waveformRef} className="w-full" />
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-secondary bg-opacity-50">
            <div className="animate-pulse text-sm" style={{ color: theme.text }}>
              Loading audio...
            </div>
          </div>
        )}
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => skipTime(-10)}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            style={{ color: theme.text }}
            disabled={isLoading}
          >
            <SkipBack size={20} />
          </button>
          
          <button
            onClick={togglePlay}
            className={`p-3 rounded-full transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ 
              backgroundColor: isLoading ? theme.secondary : theme.primary,
              color: theme.text
            }}
            disabled={isLoading}
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </button>
          
          <button
            onClick={() => skipTime(10)}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            style={{ color: theme.text }}
            disabled={isLoading}
          >
            <SkipForward size={20} />
          </button>
        </div>

        {/* Time */}
        <div className="flex items-center gap-2 text-sm" style={{ color: theme.text }}>
          <span>{formatTime(currentTime)}</span>
          <span>/</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Volume */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMute}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            style={{ color: theme.text }}
            disabled={isLoading}
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-24 accent-primary"
            style={{ 
              accentColor: theme.primary,
              opacity: isLoading ? 0.5 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer; 